import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import { TileChip } from '@rossum/components/tile';
import {
  IconBrain,
  IconCustomAurora,
  IconLayoutList,
  IconMail,
  IconSTurnRight,
  IconUserSquareRounded,
} from '@rossum/ui/icons/tabler';
import { useSchema } from '../../../../business/schema/useSchema';
import { useQueueAssignedUsersCount } from '../../../../business/users/useQueueAssignedUsersCount';
import { useEmailTemplatesPerQueueUnpaginated } from '../../../emails/email-templates/hooks/useEmailTemplatesPerQueueUnpaginated';
import { engineBasicSettingsPath } from '../../../engines/routes';
import { getSchemaDatapoints } from '../../../schemas/getSchemaDatapoints';
import { EngineChipData } from '../../types';

const CHIP_COLOR = {
  always: 'success',
  never: 'default',
  confident: 'aurora',
} as const;

const AUTOMATION_LEVEL_LABEL_ID = {
  always: 'features.queueManager.metaChips.automationLevel.always',
  confident: 'features.queueManager.metaChips.automationLevel.confident',
  never: 'features.queueManager.metaChips.automationLevel.never',
} as const;

const MAX_ENGINE_NAME_LENGTH = 20;
const AURORA_KEYWORD = 'aurora';

const isAuroraIcon = (queueEngine: EngineChipData) => {
  if (queueEngine.type === 'aurora') return true;
  if (queueEngine.type === 'generic') {
    return queueEngine.name.toLowerCase().includes(AURORA_KEYWORD);
  }
  return false;
};

type UseQueueTileChipsParams = {
  queue: Queue;
  enabled: boolean;
  engineChipData: EngineChipData | undefined;
  rulesCount: number;
};

export const useQueueTileChips = ({
  queue,
  enabled,
  engineChipData,
  rulesCount,
}: UseQueueTileChipsParams) => {
  const intl = useIntl();

  const { data: fields = [], isLoading: isSchemaLoading } = useSchema(
    enabled ? queue.schema : undefined,
    { select: getSchemaDatapoints }
  );
  const fieldsCount = fields.length;

  const { data: emailTemplates, isLoading: isEmailTemplatesLoading } =
    useEmailTemplatesPerQueueUnpaginated(enabled ? queue.id : null);
  const emailTemplatesCount = emailTemplates?.length ?? 0;

  const usersCount = useQueueAssignedUsersCount(queue);

  const automationLevelLabel = intl.formatMessage({
    id: AUTOMATION_LEVEL_LABEL_ID[queue.automationLevel],
  });

  const chips = [
    engineChipData
      ? engineChipData.isLoading
        ? { key: 'engine', label: '', isLoading: true }
        : {
            key: 'engine',
            label: R.truncate(engineChipData.name, MAX_ENGINE_NAME_LENGTH, {
              omission: '...',
            }),
            Icon: isAuroraIcon(engineChipData) ? IconCustomAurora : IconBrain,
            color: CHIP_COLOR[queue.automationLevel],
            tooltip: [
              engineChipData.name,
              automationLevelLabel,
              engineChipData.type === 'aurora'
                ? intl.formatMessage({
                    id: 'features.queueManager.metaChips.auroraEngineInfo',
                  })
                : undefined,
            ]
              .filter(Boolean)
              .join(' · '),
            to:
              engineChipData.type === 'aurora'
                ? engineBasicSettingsPath(engineChipData.id)
                : undefined,
          }
      : null,

    isSchemaLoading
      ? { key: 'fields', label: '', isLoading: true }
      : fieldsCount > 0
        ? {
            key: 'fields',
            label: String(fieldsCount),
            Icon: IconLayoutList,
            tooltip: intl.formatMessage({
              id: 'features.queueManager.metaChips.fields',
            }),
            to: `/queues/${queue.id}/settings/fields`,
          }
        : null,

    rulesCount > 0
      ? {
          key: 'rules',
          label: String(rulesCount),
          Icon: IconSTurnRight,
          tooltip: intl.formatMessage({
            id: 'features.queueManager.metaChips.rules',
          }),
          to: `/queues/${queue.id}/settings/rules`,
        }
      : null,

    isEmailTemplatesLoading
      ? { key: 'emails', label: '', isLoading: true }
      : emailTemplatesCount > 0
        ? {
            key: 'emails',
            label: String(emailTemplatesCount),
            Icon: IconMail,
            tooltip: intl.formatMessage({
              id: 'features.queueManager.metaChips.emailTemplates',
            }),
            to: `/queues/${queue.id}/settings/emails`,
          }
        : null,

    usersCount > 0
      ? {
          key: 'users',
          label: String(usersCount),
          Icon: IconUserSquareRounded,
          tooltip: intl.formatMessage({
            id: 'features.queueManager.metaChips.users',
          }),
          to: `/queues/${queue.id}/settings/access`,
        }
      : null,
  ] satisfies (TileChip | null)[];

  // Engine chip stays at the end, next to the visibility toggle; counts go next to the title
  const [endChips, inlineBadges] = R.partition(
    chips.filter(chip => chip !== null),
    chip => chip.key === 'engine'
  );

  return { endChips, inlineBadges };
};
